import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePermission from '../hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useQuery from '../../../../util/useQuery';
import { Element, Events, scroller } from 'react-scroll';

export default function List({
    getPermissionList,
    permissionList,
    isEdit,
    setIsEdit,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useQuery();

    const { loading, createdPermission } = usePermission();

    useEffect(() => {
        getPermissionList();
    }, [getPermissionList]);

    useEffect(() => {
        if (!params.get('id') && permissionList.length > 0) {
            navigate(`?id=${permissionList[0].organizePermissionId}`);
        }
    }, [navigate, params, permissionList]);

    useEffect(() => {
        return () => {
            Events.scrollEvent.remove('begin');
            Events.scrollEvent.remove('end');
        };
    }, []);

    const scrollToWithContainer = useCallback((id) => {
        const goToContainer = new Promise((resolve) => {
            Events.scrollEvent.register('end', () => {
                resolve();
                Events.scrollEvent.remove('end');
            });

            scroller.scrollTo('main-permission', {
                duration: 300,
                delay: 0,
                smooth: 'easeInOutQuart',
                offset: -100,
            });
        });

        goToContainer.then(() =>
            scroller.scrollTo(id, {
                duration: 300,
                delay: 0,
                smooth: 'easeInOutQuart',
                containerId: 'permission-list',
                offset: 0,
            })
        );
    }, []);

    const onCretedPermission = () => {
        // Do not duplicate permission name
        let permission = 'New';
        // have New permission name in permissionList then replace it with New (1), New (2), New (3), ...

        const newPermission = permissionList.find(
            (prem) => prem.permissionName === permission
        );
        if (newPermission) {
            let count = 1;
            while (true) {
                const currentPermission = `New ${count}`;
                if (
                    !permissionList.find(
                        (prem) => prem.permissionName === currentPermission
                    )
                ) {
                    permission = currentPermission;
                    break;
                }
                count++;
            }
        }

        createdPermission(
            {
                permissionName: permission,
                sequence: permissionList.length + 1,
            },
            (id) => {
                getPermissionList();
                setIsEdit(true);
                scrollToWithContainer(id);
            }
        );
    };

    return (
        <div>
            <div className="p-4 flex justify-between items-center">
                <div className="font-bold text-[#65707A]">
                    {t('TotalItems', {
                        length: permissionList.length,
                    })}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        disabled={isEdit}
                        size="large"
                        type="primary"
                        icon={<PlusCircleOutlined />}
                        onClick={onCretedPermission}
                    >
                        {t('Add')}
                    </Button>
                </div>
            </div>
            <Element
                id="permission-list"
                className="h-[60vh] overflow-auto overflow-x-hidden relative"
                style={{
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}
            >
                <Spin spinning={loading}>
                    {permissionList.map((prem) => {
                        const active =
                            params.get('id') === prem.organizePermissionId;
                        return (
                            <button
                                onClick={() => {
                                    if (isEdit) return;
                                    navigate(
                                        `?id=${prem.organizePermissionId}`
                                    );
                                }}
                                key={prem.organizePermissionId}
                                className={classNames(
                                    'hover:bg-[#F8F4FF] duration-300 w-full relative flex items-center',
                                    'text-left w-full p-4 font-semibold text-purple-PHX',
                                    {
                                        'bg-[#F8F4FF]': active,
                                        'cursor-default': !active,
                                    }
                                )}
                                style={{
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.06)',
                                }}
                            >
                                {active && (
                                    <div className="w-[6px] h-full bg-purple-PHX absolute left-0 top-0 rounded-tr-lg rounded-br-lg" />
                                )}
                                {!active && isEdit && (
                                    <div
                                        className={classNames(
                                            'absolute top-0 left-0 w-full h-full bg-[#ffffff]/80 z-20 transition duration-500',
                                            'data-[close]:opacity-0 pointer-events-none',
                                            'data-[leave]:duration-500 data-[leave]:ease-in-out data-[leave]:opacity-0'
                                        )}
                                    />
                                )}
                                <Element id={prem.organizePermissionId} />
                                {prem.permissionName}
                            </button>
                        );
                    })}
                </Spin>
            </Element>
        </div>
    );
}

// Validate props
List.propTypes = {
    getPermissionList: PropTypes.func,
    permissionList: PropTypes.array,
    setIsEdit: PropTypes.func,
    isEdit: PropTypes.bool,
};
